<script setup lang="ts">
/**
 * Section tab bar for the matter's Narratives workspace. Sits directly below
 * the app header (the matter-name bar) and to the right of the far-left icon
 * rail, switching between the two sibling sections of the workspace:
 * "Narratives" and "Findings". Each tab navigates to its own route; the tab
 * matching the current route shows the active underline.
 *
 * See docs_for_claude/figma-designs/latest/narratives-landing.png.
 */
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMatterStore } from '../../stores/matterStore';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const matterStore = useMatterStore();

interface Tab {
    key: string;
    labelKey: string;
    routeName: string;
}

const tabs: Tab[] = [
    { key: 'narratives', labelKey: 'narratives.tabs.narratives', routeName: 'narratives' },
    { key: 'findings', labelKey: 'narratives.tabs.findings', routeName: 'findings' },
];

const activeKey = computed(() => (route.name === 'findings' ? 'findings' : 'narratives'));

const selectTab = (tab: Tab) => {
    if (tab.key === activeKey.value) return;
    const matterId = matterStore.currentMatter?.RecordId;
    if (!matterId) return;
    router.push({ name: tab.routeName, params: { matterId } });
};
</script>

<template>
    <nav class="narrative-tabs" :aria-label="t('narratives.tabs.ariaLabel')">
        <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            class="narrative-tab"
            :class="{ 'narrative-tab-active': tab.key === activeKey }"
            :aria-current="tab.key === activeKey ? 'page' : undefined"
            @click="selectTab(tab)"
        >
            {{ t(tab.labelKey) }}
        </button>
    </nav>
</template>
